import { Id, SelectedRows, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { mapToBulkRequestWithFilterId } from "../../../../../shared/api/mappings";
import { contactUpdateInquiryService } from "../../../shared/api/contactUpdateInquiryService";

export const useContactUpdateInquiryContactActions = (contactUpdateInquiryId: Id, activeContactFilterId: Id | null) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const closePendingContactUpdateInquiry = useCallback(
		async (selectedRows: SelectedRows, search?: string | null) => {
			const res = await contactUpdateInquiryService.closeInquiriesForContact(
				contactUpdateInquiryId,
				mapToBulkRequestWithFilterId(selectedRows, activeContactFilterId, search),
			);

			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("Contact update inquiry"),
					body: t("Contact update successfully closed."),
				});
				void queryClient.invalidateQueries({
					queryKey: contactUpdateInquiryService.contactsWithPendingFeedbackKey(contactUpdateInquiryId),
				});
				void queryClient.invalidateQueries({
					queryKey: contactUpdateInquiryService.detailsKey(contactUpdateInquiryId),
				});
			}
		},
		[activeContactFilterId, contactUpdateInquiryId, queryClient, showToast, t],
	);

	const resendContactInquiryEmail = useCallback(
		async (selectedRows: SelectedRows, search?: string | null) => {
			const res = await contactUpdateInquiryService.resendContactUpdateEmail(
				contactUpdateInquiryId,
				mapToBulkRequestWithFilterId(selectedRows, activeContactFilterId, search),
			);

			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("Contact update inquiry"),
					body: t("Contact update inquiry email successfully sent."),
				});
			}
		},
		[activeContactFilterId, contactUpdateInquiryId, showToast, t],
	);

	return {
		closePendingContactUpdateInquiry,
		resendContactInquiryEmail,
	};
};
